import { ListChecksIcon, UserRoundSearchIcon } from "lucide-react";

import {
  FIELD_CONTENT_CLASS,
  FIELD_ICON_CLASS,
  FIELD_TRIGGER_CLASS,
  SadieSimulatorField,
  SadieSimulatorSwitchRow,
} from "./SadieSimulatorField";
import {
  SadieSimulatorJobSearchFields,
  SadieSimulatorJobSearchSetup,
} from "./SadieSimulatorJobSearchFields";
import { SadieSimulatorPipelineEntries } from "./SadieSimulatorPipelineEntries";

import { Column } from "@/components/Column";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";

import {
  SadieSimulatorMatchedOpportunityFit,
  SadieSimulatorMatchedOpportunityOption,
  SadieSimulatorPipeline,
} from "@/types";

export interface SadieSimulatorWorkerInitiatedSetup {
  onPipelineChange: (key: string) => void;
  onSession1IncompleteChange: (value: boolean) => void;
  pipelines: SadieSimulatorPipeline[];
  selectedPipeline: string;
  session1Incomplete: boolean;
}

// Session 3's setup: what the worker has in motion for a status check,
// whether they never finished Session 1, and what a job search finds when
// they ask what is open. All of it is fixture data. Nothing is looked up.
export const SadieSimulatorWorkerInitiatedFields = ({
  disabled,
  fitFor,
  jobSearchSetup,
  matchedOpportunities,
  setup,
}: {
  disabled: boolean;
  fitFor: (
    option: SadieSimulatorMatchedOpportunityOption,
  ) => SadieSimulatorMatchedOpportunityFit | undefined;
  jobSearchSetup: SadieSimulatorJobSearchSetup;
  matchedOpportunities: SadieSimulatorMatchedOpportunityOption[];
  setup: SadieSimulatorWorkerInitiatedSetup;
}) => {
  /* Helpers */
  const pipeline = setup.pipelines.find(
    (p) => p.key === setup.selectedPipeline,
  );

  /* Render */
  return (
    <Column className="gap-3">
      <SadieSimulatorField
        icon={<ListChecksIcon />}
        id="sadie-sim-pipeline"
        label="Pipeline"
      >
        <Select
          disabled={disabled}
          onValueChange={setup.onPipelineChange}
          value={setup.selectedPipeline}
        >
          <SelectTrigger
            className={FIELD_TRIGGER_CLASS}
            id="sadie-sim-pipeline"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={FIELD_CONTENT_CLASS}>
            {setup.pipelines.map((p) => (
              <SelectItem key={p.key} value={p.key}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {pipeline ? (
          <SadieSimulatorPipelineEntries entries={pipeline.entries} />
        ) : null}
      </SadieSimulatorField>

      <SadieSimulatorSwitchRow
        checked={setup.session1Incomplete}
        description="Sadie moves into the get-to-know-you questions once their ask is handled"
        disabled={disabled}
        icon={<UserRoundSearchIcon className={FIELD_ICON_CLASS} />}
        id="sadie-sim-session-1-incomplete"
        label="Never finished Session 1"
        onCheckedChange={setup.onSession1IncompleteChange}
      />

      <SadieSimulatorJobSearchFields
        alreadySearchedDescription="The search result is in the prompt from their first text"
        disabled={disabled}
        fitFor={fitFor}
        matchedOpportunities={matchedOpportunities}
        setup={jobSearchSetup}
      />
    </Column>
  );
};
